import posthog from 'posthog-js';
import constants from '@/config/constants';
import { isDemoEnvironment } from '@/services/api/environment';

// Product analytics (PostHog). Off unless VUE_APP_POSTHOG_KEY is set, and always off in the
// demo build and on localhost (`npm run serve` loads the dev key from .env.development).
// Only ids, slugs and flags are sent — never names, emails or other profile content.
let enabled = false;

const LOCAL_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];

function isLocalHost() {
  const { hostname } = window.location;
  return LOCAL_HOSTNAMES.includes(hostname) || hostname.endsWith('.localhost');
}

// URL query parameters that carry secrets or personal data: password-reset links (uid, token) and
// the profile-transfer link (email, name). PostHog masks them in every URL it records. Add any new
// sensitive parameter here when a link starts carrying one.
export const SENSITIVE_URL_PARAMS = ['uid', 'token', 'email', 'name'];

function registerEnvironment() {
  // SUBDOMAIN is app / uat / dev; VUE_APP_ENV can override it (e.g. 'local' when testing locally).
  posthog.register({ environment: constants.ENV || constants.SUBDOMAIN || 'unknown' });
}

export function initAnalytics() {
  if (!constants.POSTHOG_KEY || isDemoEnvironment() || isLocalHost()) return false;

  posthog.init(constants.POSTHOG_KEY, {
    api_host: constants.POSTHOG_HOST,
    // Pageviews are sent from the router (trackPageview) so they carry the new route's group.
    capture_pageview: false,
    person_profiles: 'identified_only',
    // Profiles hold members' personal details: autocaptured clicks keep no element text or
    // attributes, and sensitive URL parameters (plus ad click IDs) are masked.
    mask_all_text: true,
    mask_all_element_attributes: true,
    mask_personal_data_properties: true,
    custom_personal_data_properties: SENSITIVE_URL_PARAMS,
    // Replays would still record profile photos and mailto/website links (masking covers text
    // only), so recording stays off until those elements are blocked.
    disable_session_recording: true,
  });
  registerEnvironment();
  enabled = true;
  return true;
}

export function identifyUser(profile) {
  const userId = profile && profile.user && profile.user.id;
  if (!enabled || !userId) return;

  posthog.identify(String(userId), {
    is_staff: Boolean(profile.user.isStaff || profile.user.isSuperuser),
    group_count: (profile.groups || []).length,
    admin_group_count: (profile.adminOfGroups || []).length,
  });
}

// Called on every sign-out (logout and expired sessions) so the next person on this device isn't
// linked to the previous one. reset() also clears super-properties, so the environment is re-added.
export function resetAnalytics() {
  if (!enabled) return;
  posthog.reset();
  registerEnvironment();
}

// Called after each route change: tags this and following events with the group being viewed
// (or clears it outside a group), then records the pageview.
export function trackPageview(groupSlug) {
  if (!enabled) return;
  if (groupSlug) {
    posthog.register({ group_slug: groupSlug });
  } else {
    posthog.unregister('group_slug');
  }
  posthog.capture('$pageview');
}

export function track(event, properties = {}) {
  if (!enabled) return;
  posthog.capture(event, properties);
}
